import React, { useEffect, useRef, useState } from "react";
import Toast from "../components/Toast";
import API_BASE_URL from "../api";
import { authFetch } from "../authFetch";
import { useAuth } from "../authContext";
import COUNTRIES from "../constants/countries";
import { useTranslation } from "react-i18next";
import { useLocale, fmtDateTime } from "../i18n/format";

const API = (API_BASE_URL || "").replace(/\/+$/, "");

const fmtUAHLocal = (v, locale) =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "UAH",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(v || 0));

// Локальная модалка "Додати радіо" — стиль как в AddStreamModal.jsx
function AddRadioModal({
  open,
  onClose,
  name,
  setName,
  countryCode,
  setCountryCode,
  onAdd,
  adding,
  countries = [],
}) {
  const { t } = useTranslation("common");
  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "min(92vw, 560px)",
          background: "#fff",
          borderRadius: 14,
          boxShadow: "0 12px 32px rgba(0,0,0,.25)",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Шапка */}
        <div
          style={{
            background: "#00723a",
            color: "#fff",
            padding: "16px 22px",
            fontWeight: 800,
            fontSize: 20,
            letterSpacing: ".3px",
          }}
        >
          {t("radio.add.title")}
        </div>

        {/* Тело модалки */}
        <div style={{ padding: "20px 22px 22px" }}>
          {/* Назва */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 12,
            }}
          >
            <input
              type="text"
              placeholder={t("radio.add.name_placeholder")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              style={{
                flex: 1,
                padding: "12px 16px",
                fontSize: 16,
                borderRadius: 8,
                border: "1px solid #bbb",
              }}
            />
          </div>

          {/* Країна (select з прапорцями) */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 8,
            }}
          >
            <select
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              style={{
                flex: 1,
                padding: "12px 16px",
                fontSize: 16,
                borderRadius: 8,
                border: "1px solid #bbb",
                background: "#fff",
                appearance: "none",
              }}
            >
              {!countryCode && (
                <option value="" disabled>
                  {t("radio.add.country_placeholder")}
                </option>
              )}
              {countries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.flag} {c.nameUk} ({c.code})
                </option>
              ))}
            </select>
          </div>

          {/* Резерв под сообщения */}
          <div
            style={{
              minHeight: 22,
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
            }}
          />

          {/* Кнопки */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              marginTop: 10,
            }}
          >
            <button
              onClick={onClose}
              disabled={adding}
              style={{
                padding: "10px 18px",
                fontSize: 16,
                borderRadius: 8,
                border: "1px solid #ddd",
                background: "#fff",
                cursor: "pointer",
              }}
            >
              {t("common.cancel")}
            </button>
            <button
              onClick={onAdd}
              disabled={!name.trim() || !countryCode || adding}
              style={{
                padding: "10px 18px",
                fontSize: 16,
                borderRadius: 8,
                border: "none",
                background: "#00723a",
                color: "#fff",
                fontWeight: 700,
                opacity: !name.trim() || !countryCode || adding ? 0.6 : 1,
                cursor:
                  !name.trim() || !countryCode || adding
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              {adding ? t("radio.add.submitting") : t("radio.add.submit")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Коротка автозакриваюча модалка «інструкція відправлена»
function InfoModal({ open, text }) {
  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.35)",
        zIndex: 10000,
        display: "grid",
        placeItems: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          maxWidth: 420,
          width: "100%",
          background: "#fff",
          borderRadius: 15,
          boxShadow: "0 2px 16px #0002",
          padding: "26px 22px",
          textAlign: "center",
          fontSize: 18,
          fontWeight: 600,
        }}
      >
        {text}
      </div>
    </div>
  );
}

export default function RadioPage({ setCartItems, setIsCartOpen }) {
  const { isAuth, user } = useAuth();
  const isDemo = String(user?.email || "").toLowerCase() === "demo@radiomonitor.net";
  const { t } = useTranslation("common");
  const locale = useLocale();
  const toastTimer = useRef(null);
  const [toast, setToast] = useState({ open: false, text: "", type: "info" });
  const [mailSentOpen, setMailSentOpen] = useState(false);
  const [showLoginAlert, setShowLoginAlert] = useState(false);
  const showToast = (text, type = "info") => {
    setToast({ open: true, text, type });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(
      () => setToast({ open: false, text: "", type }),
      3200
    );
  };

  const ensureAuthorized = () => {
    if (!isAuth) {
      setShowLoginAlert(true);
      return false;
    }
    return true;
  };

  void setCartItems;
  void setIsCartOpen;

  // Список радіо
  const [radios, setRadios] = useState([]); // {id,name,country,status,paid_until}
  const [loadingRadios, setLoadingRadios] = useState(false);
  const [cartAddingId, setCartAddingId] = useState(null);

  // Страны (локально)
  const countries = COUNTRIES; // статичний список актуальних країн
  const [radioName, setRadioName] = useState("");
  const [radioCountryCode, setRadioCountryCode] = useState(() => {
    const ua = countries.find((c) => c.code === "UA");
    return ua ? ua.code : countries[0]?.code || "";
  });

  // Ціна STATS
  const [statsPriceUAH, setStatsPriceUAH] = useState(null);

  // Додавання
  const [addOpen, setAddOpen] = useState(false);
  const [adding, setAdding] = useState(false);

  async function fetchRadios() {
    setLoadingRadios(true);
    try {
      const res = await authFetch(`${API}/radios`);
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        setRadios(list);
        window.dispatchEvent(
          new CustomEvent("radios:data", { detail: { items: list } })
        );
      } else {
        setRadios([]);
        window.dispatchEvent(
          new CustomEvent("radios:data", { detail: { items: [] } })
        );
      }
    } catch {
      setRadios([]);
      window.dispatchEvent(
        new CustomEvent("radios:data", { detail: { items: [] } })
      );
    } finally {
      setLoadingRadios(false);
    }
  }

  async function fetchStatsPrice() {
    try {
      const r = await authFetch(`${API}/products/STATS/price-uah`);
      if (r.ok) {
        const data = await r.json();
        setStatsPriceUAH(
          typeof data?.price_uah === "number" ? data.price_uah : null
        );

      } else {
        setStatsPriceUAH(null);
      }
    } catch {
      setStatsPriceUAH(null);
    }
  }

  async function addRadioToCart(radioId) {
    if (!ensureAuthorized()) return;
    setCartAddingId(radioId);
    try {
      const res = await authFetch(`${API}/cart/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product_code: "STATS",
          radio_id: radioId,
          qty: 1,
          meta: {}, // radio_id все одно дублюється на бекенді у meta
        }),
      });

      if (res.ok) {
        window.dispatchEvent(
          new CustomEvent("cart:refresh", { detail: { openCart: false } })
        );
        showToast(t("radio.toast.added_to_cart"), "info");
        return;
      }

      let msg = `${res.status} ${res.statusText}`;
      try {
        const d = await res.json();
        if (typeof d?.detail === "string") msg = d.detail;
      } catch {}

      if (res.status === 409) {
        showToast(t("radio.toast.already_in_cart"), "info");
      } else if (res.status === 400 && /not connected|radio_id/i.test(msg)) {
        showToast(
          t("radio.toast.not_connected_payment_later"),
          "error"
        );
      } else if (res.status === 404 && /product not found/i.test(msg)) {
        showToast(t("radio.toast.product_unavailable"), "error");
      } else {
        showToast(msg || t("radio.toast.add_failed"), "error");
      }
    } catch {
      showToast(t("common.network_error"), "error");
    } finally {
      setCartAddingId(null);
    }
  }

  // Інфо-модалка 2с
  const [infoOpen, setInfoOpen] = useState(false);
  const infoTimer = useRef(null);
  const mailSentTimer = useRef(null);

  useEffect(() => {
    return () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
      if (infoTimer.current) clearTimeout(infoTimer.current);
      if (mailSentTimer.current) clearTimeout(mailSentTimer.current);
    };
  }, []);

  useEffect(() => {
    if (isAuth) setShowLoginAlert(false);
  }, [isAuth]);

  // Завантаження даних з API (список радіо + ціна)
  useEffect(() => {
    fetchRadios();
    fetchStatsPrice();

    // опціонально: автообновлення списку кожні 30с
    const id = setInterval(() => fetchRadios(), 30000);
    return () => clearInterval(id);
  }, []);

  async function handleAddRadio() {
    if (!ensureAuthorized()) return;
    setAdding(true);
    try {
      const chosen =
        countries.find((c) => c.code === radioCountryCode) || null;

      const res = await authFetch(`${API}/radios`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: radioName.trim(),
          // Бек зараз зберігає рядок — надсилаємо україномовну назву
          country: chosen ? chosen.nameUk : radioCountryCode,
        }),
      });

      if (res.status === 409) {
        showToast(t("radio.toast.duplicate_name"), "error");
        return;
      }

      if (!res.ok) {
        let data = {};
        try { data = await res.json(); } catch {}
        const detail = data?.detail;
        if (
          res.status === 403 &&
          detail &&
          (detail.code === "DEMO_ADD_BLOCKED" || detail?.error === "DEMO_ADD_BLOCKED")
        ) {
          return;
        }
        const msg =
          typeof detail === "string"
            ? detail
            : `${res.status} ${res.statusText}`;
        showToast(msg || t("radio.toast.add_failed"), "error");
        return;
      }

      await res.json().catch(() => ({}));
      setAddOpen(false);
      setRadioName("");
      setRadioCountryCode("");

      setInfoOpen(true);
      if (infoTimer.current) clearTimeout(infoTimer.current);
      infoTimer.current = setTimeout(() => setInfoOpen(false), 2000);

      await fetchRadios();
      setMailSentOpen(true);
      if (mailSentTimer.current) clearTimeout(mailSentTimer.current);
      mailSentTimer.current = setTimeout(() => setMailSentOpen(false), 2000);
    } catch {
      showToast(t("common.network_error"), "error");
    } finally {
      setAdding(false);
    }
  }

  return (
    <>
      {/* Модалки */}
      <AddRadioModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        name={radioName}
        setName={setRadioName}
        countryCode={radioCountryCode}
        setCountryCode={setRadioCountryCode}
        onAdd={handleAddRadio}
        adding={adding}
        countries={countries}
      />
      <InfoModal
        open={infoOpen}
        text={t("radio.info.sent_short")}
      />

      {/* === БЛОК: СТАТУС РАДІО (аналог «Статус потоків») === */}
      <div
        style={{
          maxWidth: 900,
          margin: "48px auto",
          background: "#fff",
          borderRadius: 16,
          boxShadow: "0 2px 12px #0001",
          padding: 36,
          fontFamily: "inherit",
        }}
      >
        <h2 style={{ fontWeight: 800, fontSize: 30, marginBottom: 22 }}>
          {t("radio.page.title")}
        </h2>

        <div
          style={{
            maxHeight: "55vh",
            overflowY: "auto",
            paddingRight: 0,
            overscrollBehavior: "contain",
          }}
        >
          {loadingRadios && radios.length === 0 && (
            <div>{t("common.loading")}</div>
          )}

          {radios.map((radio) => {
            // 1) eff: ПРИОРИТЕТ статусу бэка 'awaits_payment', даже если effective_status='connected'
            const eff =
              radio?.status === "awaits_payment"
                ? "awaits_payment"
                : radio.effective_status ||
                  (radio.channel_active ? "connected" : "inactive");

            const dotBorder =
              eff === "connected"
                ? "2px solid #5c5"
                : eff === "awaits_payment"
                ? "2px solid #f0a500"
                : eff === "pending_payment_window"
                ? "2px solid #f0a500"
                : eff === "blocked_grace"
                ? "2px solid #c55"
                : eff === "awaits_connection"
                ? "2px solid rgb(239,127,26)"
                : "2px solid #bbb";

            const fmt = (iso) => {
              try {
                return fmtDateTime(iso, locale);
              } catch {
                return iso;
              }
            };

            let caption = "";
            if (eff === "connected") {
              if (
                radio.payment_deadline_at &&
                new Date(radio.payment_deadline_at) > new Date()
              ) {
                caption = t("radio.status.connected_trial_until", {
                  dt: fmt(radio.payment_deadline_at),
                });
              } else if (radio.paid_until) {
                caption = t("radio.status.connected_paid_until", {
                  dt: fmt(radio.paid_until),
                });
              } else {
                caption = t("radio.status.connected_pay_to_avoid_block");
              }
            } else if (eff === "awaits_payment") {
              caption = radio.paid_until
                ? t("radio.status.awaits_payment_until", {
                    dt: fmt(radio.paid_until),
                  })
                : t("radio.status.awaits_payment_12h");
            } else if (eff === "awaits_connection") {
              caption = t("radio.status.awaits_connection");
            } else if (eff === "pending_payment_window") {
              // показуємо кінець тестового вікна підключення (12 год після старту)
              caption = radio.payment_deadline_at
                ? t("radio.status.test_window_until", {
                    dt: fmt(radio.payment_deadline_at),
                  })
                : t("radio.status.test_window_12h");
            } else if (eff === "blocked_grace") {
              const willBeUnlinked = radio.will_be_unlinked_at
                ? fmt(radio.will_be_unlinked_at)
                : "";
              caption = willBeUnlinked
                ? t("radio.status.blocked_grace_at", { dt: willBeUnlinked })
                : t("radio.status.blocked_grace_generic");
            } else if (eff === "unlinked") {
              caption = t("radio.status.unlinked");
            } else {
              caption = t("radio.status.disabled");
            }

            const now = Date.now();
            const paidUntilTs = radio.paid_until ? new Date(radio.paid_until).getTime() : null;

            // Показываем кнопку, если оплату нужно сделать/продовжити:
            // - у радио немає оплаченого періоду
            // - або оплачений період уже ЗАКІНЧИВСЯ
            // - або до завершення залишилося ≤ 12 год
            const expiredOrNear =
              !paidUntilTs || paidUntilTs <= now || paidUntilTs - now <= 12 * 3600 * 1000;

            // Кнопка повинна бути при connected, awaits_payment і ПІД ЧАС БЛОКУ (blocked_grace)
            const showPayBtn =
              (eff === "connected" || eff === "awaits_payment" || eff === "blocked_grace") &&
              expiredOrNear;

            return (
              <div
                key={radio.id}
                style={{
                  borderBottom: "1px solid #f0f0f0",
                  padding: "18px 0",
                  marginBottom: 6,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    marginBottom: 5,
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontWeight: 700, fontSize: 18, whiteSpace: "nowrap" }}>
                    {radio.name}
                  </span>

                  {/* статусна крапка + підпис (ефективний статус з бекенду) */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                      flexGrow: 1,
                      flexShrink: 1,
                      flexBasis: 280,
                      minWidth: 220,
                    }}
                  >
                    <span
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        background: dotBorder.replace("2px solid ", ""),
                        border: dotBorder,
                        display: "inline-block",
                        flexShrink: 0,
                      }}
                      title={eff}
                    />
                    <span
                      style={{
                        color: "#555",
                        fontSize: 15,
                        lineHeight: 1.45,
                        display: "inline-block",
                        maxWidth: 460,
                      }}
                    >
                      {caption}
                    </span>
                  </div>

                  {radio.country ? (
                    <span
                      style={{
                        color: "#666",
                        fontSize: 15,
                        textAlign: "center",
                        flexGrow: 1,
                        flexBasis: 220,
                        alignSelf: "center",
                      }}
                    >
                      {`${t("radio.country_label")}: ${radio.country}`}
                    </span>
                  ) : (
                    <span style={{ flexGrow: 1, flexBasis: 220 }} />
                  )}

                  {showPayBtn ? (
                    <button
                      onClick={() => addRadioToCart(radio.id)}
                      disabled={cartAddingId === radio.id || statsPriceUAH == null}
                      style={{
                        marginLeft: "auto",
                        padding: "10px 18px",
                        fontSize: 16,
                        borderRadius: 8,
                        border: "none",
                        background: "#00723a",
                        color: "#fff",
                        fontWeight: 700,
                        width: 220,
                        textAlign: "center",
                        lineHeight: 1.25,
                        cursor:
                          cartAddingId === radio.id || statsPriceUAH == null
                            ? "not-allowed"
                            : "pointer",
                        opacity:
                          cartAddingId === radio.id || statsPriceUAH == null
                            ? 0.7
                            : 1,
                        alignSelf: "flex-start",
                      }}
                      title={t("radio.cart.add_to_cart")}
                    >
                      {cartAddingId === radio.id ? (
                        t("radio.cart.adding")
                      ) : statsPriceUAH == null ? (
                        t("radio.cart.price_loading")
                      ) : (
                        <>
                          <span>{t("radio.cart.add_to_cart")}</span>
                          <span style={{ display: "block", fontWeight: 600 }}>
                            — {fmtUAHLocal(statsPriceUAH, locale)}
                          </span>
                        </>
                      )}
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}


          {!loadingRadios && radios.length === 0 && (
            <div style={{ color: "#555" }}>
              {t("radio.page.empty")}
            </div>
          )}
        </div>

        {/* Кнопка як у блоці «Статус потоків» — внизу праворуч */}
        <div style={{ marginTop: 20, textAlign: "right" }}>
          <button
            onClick={() => {
              if (isDemo) {
                window.dispatchEvent(new CustomEvent("demo:block", { detail: { action: "add_radio" } }));
                return;
              }
              setAddOpen(true);
            }}
            style={{
              padding: "10px 18px",
              fontSize: 16,
              borderRadius: 8,
              border: "none",
              background: "#00723a",
              color: "#fff",
              fontWeight: 700,
              whiteSpace: "nowrap",
              width: 220,
              cursor: "pointer",
            }}
            title={t("radio.add.open")}
          >
            {t("radio.add.open")}
          </button>
        </div>
      </div>

      {/* Модалка "лист відправлено" */}
      {mailSentOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.35)",
            zIndex: 9999,
            display: "grid",
            placeItems: "center",
            padding: 16,
          }}
          onClick={() => setMailSentOpen(false)}
        >
          <div
            style={{
              maxWidth: 420,
              width: "100%",
              background: "#fff",
              borderRadius: 15,
              boxShadow: "0 2px 16px #0002",
              padding: "0 0 26px 0",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                background: "#00723a",
                color: "#fff",
                padding: "18px 0",
                borderRadius: "15px 15px 0 0",
                textAlign: "center",
                fontSize: 22,
                fontWeight: 800,
                letterSpacing: ".3px",
              }}
            >
              {t("radio.info.sent_title")}
            </div>
            <div
              style={{
                textAlign: "center",
                margin: "24px 18px 8px",
                fontSize: 16,
                color: "#333",
              }}
            >
              {t("radio.info.sent_body")}
              <br />
              {t("radio.info.autoclose_note")}
            </div>
          </div>
        </div>
      )}

      {/* Оверлей «увійдіть» */}
      {showLoginAlert && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.35)",
            zIndex: 9999,
            display: "grid",
            placeItems: "center",
            padding: 16,
          }}
          onClick={() => setShowLoginAlert(false)}
        >
          <div
            style={{
              maxWidth: 420,
              width: "100%",
              background: "#fff",
              borderRadius: 15,
              boxShadow: "0 2px 16px #0002",
              padding: "0 0 32px 0",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                background: "#00723a",
                color: "#fff",
                padding: "18px 0",
                borderRadius: "15px 15px 0 0",
                textAlign: "center",
                fontSize: 28,
                fontWeight: 700,
                letterSpacing: "1px",
              }}
            >
              {t("radio.login_required.title")}
            </div>
            <div style={{ textAlign: "center", margin: "30px 0 26px 0", fontSize: 17 }}>
              {t("radio.login_required.desc")}
            </div>
            <div style={{ textAlign: "center" }}>
              <button
                onClick={() => setShowLoginAlert(false)}
                style={{
                  minWidth: 120,
                  padding: "12px 0",
                  background: "#00723a",
                  color: "#fff",
                  borderRadius: 7,
                  border: "none",
                  fontSize: 17,
                  fontWeight: 600,
                  cursor: "pointer",
                  marginTop: 8,
                }}
              >
                {t("common.ok")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Тости */}
      <Toast
        open={toast.open}
        text={toast.text}
        type={toast.type}
        onClose={() => setToast({ open: false, text: "", type: "info" })}
      />
    </>
  );
}
